import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react"
import type { KeyboardEvent } from "react"
import { Button, RadioGroup, Text, TextField, Textarea } from "@smooth/design-system-components"
import { chatBubbleClass } from "../../components/DunningChatPanel"
import { DETAIL_SPLIT_QUERY } from "../../components/detailLayout"
import { jstDate } from "../caseFacts"
import {
  MAX_BODY_LENGTH,
  MAX_MESSAGES,
  appendedAt,
  dayLabel,
  insertedAt,
  jstTime,
  messageProblem,
  parseConversationText,
  sortConversation,
} from "../conversationEditing"
import type { ScenarioMessage } from "../types"
import { fromLocalInput, toLocalInput } from "./ScenarioRecordsForm"
import { AlertMessage, StatusMessage, TextButton } from "./parts"

type Props = {
  conversation: ScenarioMessage[]
  /** 基準日時。これより後の発言は保存できない */
  asOf: string
  /** 保存・複製の実行中など、操作を受け付けない間は true */
  busy: boolean
  /** 会話全体のエラー（保存できない理由） */
  error?: string
  /** 日時順に並べた会話を返す（保存形式は既存のフォームと同じ） */
  onChange: (conversation: ScenarioMessage[]) => void
}

const SPEAKER_LABELS: Record<ScenarioMessage["direction"], string> = {
  inbound: "お客様",
  outbound: "当社",
}

const opposite = (direction: ScenarioMessage["direction"]) =>
  direction === "inbound" ? "outbound" : "inbound"

/** ⌘/Ctrl+Enter（変換中の Enter は除く） */
const isSubmitKey = (e: KeyboardEvent) =>
  e.key === "Enter" && (e.metaKey || e.ctrlKey) && !e.nativeEvent.isComposing

/** 入力に合わせて高さを伸ばす（手で伸ばす代わり） */
function useAutoHeight(value: string) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    element.style.height = "auto"
    element.style.height = `${element.scrollHeight + 2}px`
  }, [value])
  return ref
}

/** xl で会話を入れている左のペイン（縦にスクロールする一番近い祖先）を末尾まで送る */
function scrollPaneToEnd(element: HTMLElement | null) {
  if (!element || !window.matchMedia?.(DETAIL_SPLIT_QUERY)?.matches) return
  for (
    let node = element.parentElement;
    node && node !== document.body;
    node = node.parentElement
  ) {
    if (/(auto|scroll)/.test(window.getComputedStyle(node).overflowY)) {
      node.scrollTop = node.scrollHeight
      return
    }
  }
}

/** 吹き出しの中身。画像は「（画像）」と補足を出す */
function BubbleContent({ message }: { message: ScenarioMessage }) {
  if (message.contentType === "image")
    return (
      <>
        <span className="font-bold">（画像）</span>
        {message.body ? `\n${message.body}` : ""}
      </>
    )
  if (message.body) return <>{message.body}</>
  return <span className="opacity-60">（本文を入力）</span>
}

/**
 * 本文をその場で書き換える入力欄。⌘/Ctrl+Enter か外側のクリックで閉じ、Esc で開く前の本文に戻す。
 * 打った本文はその都度 onInput で返す（閉じる前に「ケースを保存」を押しても、保存できるかの判定と保存内容に入っている）
 */
function BodyInput({
  number,
  message,
  onInput,
  onCommit,
  onCancel,
}: {
  number: number
  message: ScenarioMessage
  onInput: (body: string) => void
  onCommit: (body: string) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState(message.body)
  const ref = useAutoHeight(draft)
  // 確定・取り消しの後に外れたフォーカス（blur）で二重に閉じない
  const closed = useRef(false)
  const close = (commit: boolean) => {
    if (closed.current) return
    closed.current = true
    if (commit) onCommit(draft)
    else onCancel()
  }
  useEffect(() => {
    const element = ref.current
    if (!element) return
    element.focus()
    element.setSelectionRange(element.value.length, element.value.length)
  }, [ref])
  return (
    <Textarea
      ref={ref}
      aria-label={`発言 ${number} の本文`}
      rows={1}
      maxLength={MAX_BODY_LENGTH}
      value={draft}
      className="min-w-[16rem] resize-none"
      placeholder={
        message.contentType === "image" ? "画像についての補足（任意）" : "会話内容を入力"
      }
      onChange={(e) => {
        setDraft(e.target.value)
        onInput(e.target.value)
      }}
      onKeyDown={(e) => {
        if (isSubmitKey(e)) {
          e.preventDefault()
          close(true)
        } else if (e.key === "Escape" && !e.nativeEvent.isComposing) {
          // 変換中の Esc は変換の取り消しなので、本文は戻さない
          e.preventDefault()
          close(false)
        }
      }}
      onBlur={() => close(true)}
    />
  )
}

/** 発言の日時を変える小さな欄。変えると日時順に並べ直す */
function TimeEditor({
  number,
  value,
  onApply,
  onClose,
}: {
  number: number
  value: string
  onApply: (at: string) => void
  onClose: () => void
}) {
  // 欄は分まで。開いたときの値から変えずに「変更」したら、元の日時（秒・時差の書き方）をそのまま残す
  const initial = toLocalInput(value)
  const [draft, setDraft] = useState(initial)
  return (
    <div
      role="dialog"
      aria-label={`発言 ${number} の日時を変更`}
      className="mt-1 flex flex-wrap items-center gap-2 rounded-md border border-border-default bg-bg-default p-2 shadow-sm"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose()
      }}
    >
      <div className="w-[13rem]">
        <TextField
          type="datetime-local"
          aria-label={`発言 ${number} の日時`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
      </div>
      <Button
        variant="outline"
        disabled={!draft}
        onClick={() => {
          if (draft !== initial) onApply(fromLocalInput(draft))
          onClose()
        }}
      >
        変更
      </Button>
      <TextButton onClick={onClose}>やめる</TextButton>
    </div>
  )
}

/** 貼り付けた会話テキストをまとめて取り込む */
function PasteImporter({
  conversation,
  asOf,
  busy,
  onImport,
}: {
  conversation: ScenarioMessage[]
  asOf: string
  busy: boolean
  onImport: (conversation: ScenarioMessage[]) => void
}) {
  const [text, setText] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [imported, setImported] = useState<number | null>(null)
  // 「今の会話と置き換える」は今の会話を捨てるので、もう一度押して確かめる
  const [confirmingReplace, setConfirmingReplace] = useState(false)
  const run = (mode: "append" | "replace") => {
    setConfirmingReplace(false)
    const base = mode === "append" ? conversation : []
    const parsed = parseConversationText(text, { startAt: base[base.length - 1]?.at, asOf })
    setImported(null)
    if ("error" in parsed) return setError(parsed.error)
    if (base.length + parsed.messages.length > MAX_MESSAGES)
      return setError(
        `会話は${MAX_MESSAGES}件までです（取り込むと${base.length + parsed.messages.length}件）。`
      )
    setError(null)
    setText("")
    setImported(parsed.messages.length)
    onImport(sortConversation([...base, ...parsed.messages]))
  }
  return (
    <details className="border-t border-border-default px-5 py-3">
      <summary className="cursor-pointer text-body font-bold text-text-default">
        会話をまとめて貼り付ける
      </summary>
      <div className="flex flex-col gap-2 pt-2">
        <Text size="sm">
          1行ずつ「お客様: 本文」「当社:
          本文」の形で貼り付けます。話者の無い行は直前の発言の続き（改行）です。行頭に「2026/09/19
          12:00」があればその日時、無ければ直前の発言の10分後にします。「（画像）」で始まる発言は画像にします。
        </Text>
        <Textarea
          aria-label="貼り付ける会話"
          rows={5}
          value={text}
          placeholder={"お客様: 今月は払えそうにありません\n当社: ご連絡ありがとうございます。"}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={busy || !text.trim()} onClick={() => run("append")}>
            末尾に追加
          </Button>
          <Button
            variant="outline"
            disabled={busy || !text.trim()}
            onClick={() => (conversation.length > 0 ? setConfirmingReplace(true) : run("replace"))}
          >
            今の会話と置き換える
          </Button>
        </div>
        {confirmingReplace && (
          <div
            role="alertdialog"
            aria-label="会話の置き換えの確認"
            className="flex flex-wrap items-center gap-2 rounded-md border border-border-default p-2"
          >
            <Text as="span" size="sm">
              今の会話{conversation.length}件を消して、貼り付けた会話に置き換えますか？
            </Text>
            <Button variant="outline" disabled={busy} onClick={() => run("replace")}>
              置き換える
            </Button>
            <TextButton onClick={() => setConfirmingReplace(false)}>やめる</TextButton>
          </div>
        )}
        {error && <AlertMessage>{error}</AlertMessage>}
        {imported != null && <StatusMessage>{imported}件の発言を取り込みました。</StatusMessage>}
      </div>
    </details>
  )
}

/**
 * 検証ケースの督促チャット（LINE）の編集。督促チャットと同じ吹き出しで並べ、吹き出しをクリックして本文をその場で書き換える。
 * 吹き出しにホバー（またはフォーカス）すると日時・発言者・種別の変更、削除、下への挿入ができ、下の入力欄から末尾に足せる。
 * 会話はいつも日時順（同時刻は並びのまま）で返す。
 * 中にスクロール枠は作らない（xl では左のペインごとスクロールし、見出しはペインの上端・追加の欄は下端に残す）
 */
export function ConversationEditor({ conversation, asOf, busy, error, onChange }: Props) {
  const saved = sortConversation(conversation)
  // 「この下に挿入」で作り、まだ何も書いていない発言。書くまで会話（onChange）には入れない（取り消しで変更ありにしない）
  const [pending, setPending] = useState<{ index: number; message: ScenarioMessage } | null>(null)
  const messages = pending
    ? [...saved.slice(0, pending.index), pending.message, ...saved.slice(pending.index)]
    : saved
  // 発言の識別子（保存形式には持たないので、この画面の中だけで振る）。吹き出しの key と編集中の発言の識別に使い、
  // 挿入した空の発言が消えて並びの位置がずれても、押した吹き出しと操作の対象が食い違わないようにする
  const ids = useRef(new WeakMap<ScenarioMessage, number>())
  const lastId = useRef(0)
  const idOf = (message: ScenarioMessage) => {
    let id = ids.current.get(message)
    if (id == null) {
      id = ++lastId.current
      ids.current.set(message, id)
    }
    return id
  }
  /** 書き換えた発言に元の発言の識別子を引き継ぐ */
  const carry = (from: ScenarioMessage, to: ScenarioMessage) => {
    ids.current.set(to, idOf(from))
    return to
  }
  // 本文を書き換え中の発言。original は開いたときの本文（Esc で戻す）、inserted は挿入したばかりの発言で、空のまま閉じたら消す
  const [editing, setEditing] = useState<{
    id: number
    original: string
    inserted: boolean
  } | null>(null)
  const [timeEditing, setTimeEditing] = useState<number | null>(null)
  const [composer, setComposer] = useState<{
    direction: ScenarioMessage["direction"]
    body: string
    /** 手で変えた日時。null なら直前の発言の10分後 */
    at: string | null
  }>({ direction: "inbound", body: "", at: null })
  const composerRef = useAutoHeight(composer.body)
  const listRef = useRef<HTMLDivElement>(null)
  // 末尾に足したときだけ一番下まで送る（挿入・並べ替えでは書いている場所から動かさない）
  const scrollToEnd = useRef(false)
  const full = messages.length >= MAX_MESSAGES
  const composerAt = composer.at ?? appendedAt(saved[saved.length - 1]?.at, asOf)

  useEffect(() => {
    if (!scrollToEnd.current) return
    scrollToEnd.current = false
    scrollPaneToEnd(listRef.current)
  })

  const emit = (next: ScenarioMessage[]) => onChange(sortConversation(next))
  const isPending = (message: ScenarioMessage) => message === pending?.message
  // 挿入したまま書いていない発言は会話に入れない
  const edit = (target: ScenarioMessage, patch: Partial<ScenarioMessage>) =>
    emit(
      saved.map((message) =>
        message === target ? carry(message, { ...message, ...patch }) : message
      )
    )
  const remove = (target: ScenarioMessage) => {
    setEditing(null)
    setTimeEditing(null)
    if (isPending(target)) return setPending(null)
    emit(saved.filter((message) => message !== target))
  }
  /** 書き換え中の本文。挿入したばかりの発言は、空でない本文を書いた時点で会話に入れる */
  const input = (target: ScenarioMessage, body: string) => {
    if (pending && isPending(target)) {
      if (!body.trim())
        return setPending({ ...pending, message: carry(target, { ...target, body }) })
      setPending(null)
      // 並べ替えない（差し込む日時は前後の発言の間）ので、差し込んだ位置のまま
      return onChange(
        messages.map((message) =>
          message === target ? carry(message, { ...message, body }) : message
        )
      )
    }
    edit(target, { body })
  }
  const insertBelow = (source: ScenarioMessage) => {
    // 位置は挿入中の発言を含まない会話（saved）で決める
    const index = saved.indexOf(source)
    if (index < 0) return
    const message: ScenarioMessage = {
      at: insertedAt(source.at, saved[index + 1]?.at, asOf),
      direction: opposite(source.direction),
      contentType: "text",
      body: "",
    }
    setTimeEditing(null)
    setPending({ index: index + 1, message })
    setEditing({ id: idOf(message), original: "", inserted: true })
  }
  const append = (contentType: ScenarioMessage["contentType"]) => {
    if (busy || full) return
    if (contentType === "text" && !composer.body.trim()) return
    scrollToEnd.current = true
    emit([
      ...saved,
      { at: composerAt, direction: composer.direction, contentType, body: composer.body },
    ])
    setComposer({ ...composer, body: "", at: null })
  }

  return (
    <section
      aria-label="督促チャット"
      className="flex min-w-0 flex-col rounded-xl border border-gray-200 bg-white shadow-sm xl:shrink-0 xl:grow"
    >
      <div className="flex items-baseline justify-between gap-2 rounded-t-xl border-b border-gray-200 bg-white px-5 py-3 xl:sticky xl:top-0 xl:z-20">
        <span className="text-[1.3rem] font-bold text-gray-900">💬 督促チャット（LINE）</span>
      </div>

      <div
        ref={listRef}
        // 編集領域なので live region（role="log"）にしない（追加・削除・切り替えのたびに読み上げない）
        role="group"
        aria-label="会話"
        className="mx-5 mt-3 flex min-h-[160px] grow flex-col gap-3 overflow-x-clip rounded-lg bg-[#eef2f4] px-4 py-5"
      >
        {messages.length === 0 && (
          <p className="py-8 text-center text-[1.3rem] text-black">
            会話はありません（当社からの案内）。下の欄から発言を足せます。
          </p>
        )}
        {messages.map((message, index) => {
          const number = index + 1
          const inbound = message.direction === "inbound"
          // 挿入して書き始める前の発言はまだ会話に入っていない（保存も止めない）ので、問題は出さない
          const problem = isPending(message) ? null : messageProblem(message, asOf)
          const newDay = index === 0 || jstDate(messages[index - 1].at) !== jstDate(message.at)
          const id = idOf(message)
          const isEditing = editing?.id === id
          return (
            <Fragment key={id}>
              {newDay && (
                <div
                  role="separator"
                  // separator は中の文字を読み上げないので、日付は名前として持たせる
                  aria-label={dayLabel(message.at)}
                  className="flex items-center gap-3 py-1"
                >
                  <span className="h-px flex-1 bg-gray-300" />
                  <span className="text-[1.125rem] text-gray-600">{dayLabel(message.at)}</span>
                  <span className="h-px flex-1 bg-gray-300" />
                </div>
              )}
              <div
                role="group"
                aria-label={`発言 ${number}（${SPEAKER_LABELS[message.direction]}）`}
                className={`group relative flex max-w-[78%] flex-col gap-0.5 ${
                  inbound ? "items-start self-start" : "items-end self-end"
                } ${isEditing ? "w-[78%]" : ""}`}
              >
                {isEditing ? (
                  <div className="w-full">
                    <BodyInput
                      number={number}
                      message={message}
                      onInput={(body) => input(message, body)}
                      onCommit={(body) => {
                        setEditing(null)
                        // 挿入したまま何も書かずに離れたら、挿入しなかったことにする
                        if (editing?.inserted && !body.trim()) remove(message)
                      }}
                      onCancel={() => {
                        setEditing(null)
                        if (editing?.inserted) remove(message)
                        else if (editing && message.body !== editing.original)
                          edit(message, { body: editing.original })
                      }}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    aria-label={`発言 ${number} を編集`}
                    disabled={busy}
                    onClick={() => {
                      setTimeEditing(null)
                      setEditing({ id, original: message.body, inserted: false })
                    }}
                    className={[
                      "whitespace-pre-wrap break-words px-4 py-2.5 text-left text-[1.4rem] leading-relaxed shadow-sm",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring",
                      "enabled:cursor-text",
                      chatBubbleClass(message.direction),
                      problem ? "ring-2 ring-text-error" : "",
                    ].join(" ")}
                  >
                    <BubbleContent message={message} />
                  </button>
                )}
                <div className="flex items-center gap-2 px-1 text-[1.125rem] text-black">
                  <span>{jstTime(message.at)}</span>
                  {problem && <span className="font-bold text-text-error">{problem}</span>}
                </div>
                {!busy && !isEditing && (
                  <div
                    role="toolbar"
                    aria-label={`発言 ${number} の操作`}
                    // 吹き出しの上に重ねて出す（出し入れで会話の行がずれないように）。隠れている間はクリックを通す
                    className={[
                      "pointer-events-none absolute bottom-full z-10 -mb-1 flex gap-3 whitespace-nowrap rounded-md border border-border-default bg-bg-default px-2 py-0.5 opacity-0 shadow-sm transition-opacity",
                      "focus-within:pointer-events-auto focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100",
                      inbound ? "left-0" : "right-0",
                    ].join(" ")}
                  >
                    <TextButton
                      aria-label={`発言 ${number} の日時を変更`}
                      onClick={() => setTimeEditing(timeEditing === id ? null : id)}
                    >
                      日時
                    </TextButton>
                    <TextButton
                      aria-label={`発言 ${number} を${SPEAKER_LABELS[opposite(message.direction)]}の発言にする`}
                      onClick={() => edit(message, { direction: opposite(message.direction) })}
                    >
                      {inbound ? "当社にする" : "お客様にする"}
                    </TextButton>
                    <TextButton
                      aria-label={`発言 ${number} を${message.contentType === "image" ? "テキスト" : "画像"}にする`}
                      onClick={() =>
                        edit(message, {
                          contentType: message.contentType === "image" ? "text" : "image",
                        })
                      }
                    >
                      {message.contentType === "image" ? "テキストにする" : "画像にする"}
                    </TextButton>
                    <TextButton
                      aria-label={`発言 ${number} の下に発言を挿入`}
                      disabled={full}
                      onClick={() => insertBelow(message)}
                    >
                      下に挿入
                    </TextButton>
                    <TextButton
                      aria-label={`発言 ${number} を削除`}
                      onClick={() => remove(message)}
                    >
                      削除
                    </TextButton>
                  </div>
                )}
                {timeEditing === id && (
                  <TimeEditor
                    number={number}
                    value={message.at}
                    onApply={(at) => edit(message, { at })}
                    onClose={() => setTimeEditing(null)}
                  />
                )}
              </div>
            </Fragment>
          )
        })}
      </div>

      <div
        role="group"
        aria-label="発言を追加"
        className="mt-3 flex flex-col gap-2 border-t border-gray-200 bg-white px-5 py-3 xl:sticky xl:bottom-0 xl:z-20"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <RadioGroup
            name="conversation-composer-direction"
            aria-label="追加する発言の話者"
            appearance="chip"
            value={composer.direction}
            onChange={(value) =>
              setComposer({ ...composer, direction: value as ScenarioMessage["direction"] })
            }
            options={[
              { value: "inbound", label: "お客様として" },
              { value: "outbound", label: "当社として" },
            ]}
          />
          <div className="w-[13rem]">
            <TextField
              type="datetime-local"
              aria-label="追加する発言の日時"
              value={toLocalInput(composerAt)}
              onChange={(e) =>
                setComposer({
                  ...composer,
                  at: e.target.value ? fromLocalInput(e.target.value) : null,
                })
              }
            />
          </div>
        </div>
        <div className="flex items-end gap-2">
          <Textarea
            ref={composerRef}
            aria-label="追加する発言の本文"
            rows={1}
            maxLength={MAX_BODY_LENGTH}
            className="resize-none"
            disabled={busy || full}
            value={composer.body}
            placeholder={
              full ? `会話は${MAX_MESSAGES}件までです` : "本文を入力（⌘/Ctrl+Enter で追加）"
            }
            onChange={(e) => setComposer({ ...composer, body: e.target.value })}
            onKeyDown={(e) => {
              if (isSubmitKey(e)) {
                e.preventDefault()
                append("text")
              }
            }}
          />
          <Button disabled={busy || full || !composer.body.trim()} onClick={() => append("text")}>
            追加
          </Button>
        </div>
        <div>
          <TextButton disabled={busy || full} onClick={() => append("image")}>
            画像として追加（本文は補足）
          </TextButton>
        </div>
      </div>

      <PasteImporter
        conversation={saved}
        asOf={asOf}
        busy={busy}
        onImport={(next) => {
          scrollToEnd.current = true
          setPending(null)
          setEditing(null)
          setTimeEditing(null)
          onChange(next)
        }}
      />
      {error && (
        <div className="px-5 pb-3">
          <AlertMessage>{error}</AlertMessage>
        </div>
      )}
    </section>
  )
}
